import { getAccessToken } from '../../Services/getAccessToken';
import { ENVIRONMENT_URL, DATAVERSE_API_VERSION } from '../../constants/dataverse';
import { normalizeGuid } from '../../utils/guid';

export { DATAVERSE_API_VERSION } from '../../constants/dataverse';

export const ODATA_JSON_HEADERS = {
  Accept: 'application/json',
  'OData-MaxVersion': '4.0',
  'OData-Version': '4.0',
} as const;

export interface FetchQualityTourRecordsOptions {
  tableName: string;
  selectFields: string | string[];
  qualityTourId: string;
  optionalAccessToken?: string | null;
  apiVersion?: string;
}

/**
 * Fetches OData records for a quality tour with GUID normalization and
 * lookup-field filter fallback (_cr3ea_qualitytourid_value).
 * Returns the raw `value` array from the Dataverse response.
 */
export async function fetchQualityTourRecords(
  options: FetchQualityTourRecordsOptions
): Promise<Record<string, unknown>[]> {
  const {
    tableName,
    selectFields,
    qualityTourId,
    optionalAccessToken,
    apiVersion = DATAVERSE_API_VERSION,
  } = options;

  let accessToken: string | undefined = optionalAccessToken ?? undefined;
  if (!accessToken) {
    const tokenResult = await getAccessToken();
    accessToken = tokenResult?.token;
  }
  if (!accessToken) {
    throw new Error('Access token is invalid or missing');
  }

  const headers = {
    ...ODATA_JSON_HEADERS,
    Authorization: `Bearer ${accessToken}`,
  };

  const select =
    typeof selectFields === 'string' ? selectFields : selectFields.join(',');
  const qualityTourGuid = normalizeGuid(qualityTourId);

  const buildUrl = (filter: string) =>
    `${ENVIRONMENT_URL}/api/data/v${apiVersion}/${tableName}` +
    `?$filter=${encodeURIComponent(filter)}` +
    `&$select=${select}`;

  let filter = `cr3ea_qualitytourid eq '${qualityTourGuid}'`;
  let response = await fetch(buildUrl(filter), { headers });

  if (!response.ok) {
    const errText = await response.text();
    if (
      errText.includes('Unrecognized') ||
      errText.includes('Edm.') ||
      errText.includes('$filter')
    ) {
      filter = `_cr3ea_qualitytourid_value eq '${qualityTourGuid}'`;
      response = await fetch(buildUrl(filter), { headers });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch data (retry): ${response.status} - ${await response.text()}`
        );
      }
    } else {
      throw new Error(`Failed to fetch data: ${response.status} - ${errText}`);
    }
  }

  const data = await response.json();
  return (data.value || []) as Record<string, unknown>[];
}
